import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, X } from "@phosphor-icons/react";
import { loadAnalytics } from "../lib/analytics";

const STORAGE_KEY = "hc_cookie_consent";

let _reopen = null;
/** Reopen the consent banner (footer "cookie settings" link). */
export function openCookieSettings() {
    if (_reopen) _reopen();
}

// Pages where a bottom-fixed banner would overlap the page's own bottom-fixed UI
// (chat composer, etc). We just skip the banner there — the user can still find
// the cookie policy via the global footer link.
const SKIP_PATHS = ["/chat", "/settings"];

/**
 * Minimal first-party cookie consent banner. Persists the choice in localStorage so it
 * never re-prompts. Three actions: accept-all, only-necessary (same as accept since we
 * use no advertising/tracking cookies — but it gives the user agency), and a dismiss X
 * that defaults to only-necessary.
 */
export default function CookieConsent() {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        _reopen = () => setOpen(true);
        return () => { _reopen = null; };
    }, []);

    useEffect(() => {
        try {
            // Cookie is the durable source (Safari wipes localStorage after ~7 days);
            // localStorage kept as a secondary for older saved choices.
            const cookieChoice = document.cookie.split("; ").find((c) => c.startsWith(`${STORAGE_KEY}=`))?.split("=")[1];
            const lsRaw = localStorage.getItem(STORAGE_KEY);
            const lsChoice = lsRaw ? JSON.parse(lsRaw).value : null;
            const choice = cookieChoice || lsChoice;
            if (!choice) {
                // Show after a short delay so the page paints first
                const t = setTimeout(() => setOpen(true), 800);
                return () => clearTimeout(t);
            }
            // Re-sync both stores so the banner stays gone on every browser.
            persist(choice);
            if (choice === "accepted") loadAnalytics();
        } catch { /* private mode */ }
        return undefined;
    }, []);

    const persist = (value) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ value, ts: new Date().toISOString() }));
        } catch { /* private mode */ }
        try {
            document.cookie = `${STORAGE_KEY}=${value}; max-age=31536000; path=/; SameSite=Lax`;
        } catch { /* noop */ }
    };

    const decide = (value) => {
        persist(value);
        if (value === "accepted") loadAnalytics();
        setOpen(false);
    };

    if (!open) return null;
    if (SKIP_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return null;

    return (
        <div
            role="dialog"
            aria-live="polite"
            aria-label="Cookie consent"
            data-testid="cookie-consent"
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 max-w-md border border-white/10 bg-[#070a10]/95 backdrop-blur rounded-md overflow-hidden shadow-2xl shadow-black/60"
        >
            <div className="h-px bg-gradient-to-r from-white/15 to-transparent" aria-hidden="true" />
            <div className="p-3 sm:p-4">
            <button
                onClick={() => decide("dismissed")}
                aria-label="Dismiss"
                data-testid="cookie-dismiss"
                className="absolute top-1.5 right-1 text-slate-500 hover:text-slate-300 transition-colors inline-flex items-center justify-center w-11 h-11 rounded-sm hover:bg-slate-800/60"
            >
                <X size={13} weight="bold" />
            </button>

            <div className="flex items-start gap-3">
                <span className="hidden sm:inline-flex shrink-0 items-center justify-center w-8 h-8 rounded-sm border border-white/10 bg-white/[0.04]">
                    <Cookie size={15} weight="duotone" className="text-slate-400" />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[9px] uppercase tracking-[0.25em] font-mono text-slate-500 whitespace-nowrap">// cookies</span>
                        <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
                    </div>
                    <h2 className="text-sm font-bold text-slate-100 mt-1" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                        we run on minimal cookies<span className="text-slate-500">.</span>
                    </h2>
                    <p className="text-[11px] sm:text-xs text-slate-400 mt-1.5 leading-relaxed">
                        One session cookie keeps you signed in. Optional analytics load{" "}
                        <strong className="text-slate-300">only if you accept</strong> — never ad trackers.{" "}
                        <span className="hidden sm:inline">
                            <Link to="/legal/cookies" className="text-slate-300 hover:text-white underline underline-offset-2" data-testid="cookie-policy-link">
                                Cookie Policy
                            </Link>{" · "}
                            <Link to="/legal/privacy" className="text-slate-300 hover:text-white underline underline-offset-2">
                                Privacy Policy
                            </Link>
                        </span>
                        <Link to="/legal/cookies" className="sm:hidden text-slate-300 hover:text-white underline underline-offset-2">details</Link>
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-3.5">
                <button
                    onClick={() => decide("accepted")}
                    data-testid="cookie-accept"
                    className="flex-1 bg-slate-100 hover:bg-white text-slate-950 font-bold text-[10px] uppercase tracking-widest px-3 py-2.5 min-h-[44px] rounded-sm transition-colors whitespace-nowrap"
                >
                    accept · continue
                </button>
                <button
                    onClick={() => decide("necessary-only")}
                    data-testid="cookie-necessary"
                    className="flex-1 border border-slate-700 hover:border-white/30 text-slate-300 hover:text-white font-bold text-[10px] uppercase tracking-widest px-3 py-2.5 min-h-[44px] rounded-sm transition-colors whitespace-nowrap"
                >
                    necessary only
                </button>
            </div>
            </div>
        </div>
    );
}
